/**
 * Parameters for building the version string
 */
interface BuildVersionStringParams {
  /** App version from package.json */
  version: string;
  /** Environment name (dev, staging, production) */
  environment: string | undefined;
  /** Runtime version for OTA compatibility */
  runtimeVersion: string | null;
  /** Native build version code */
  nativeBuildVersion: string | null;
  /** EAS Update channel (optional, for debugging) */
  channel?: string | null;
}

/**
 * Builds a formatted version string for display in the app header.
 * Format: v{version} {env} {runtimeVersion} Build: {buildVersion} [ch:{channel}]
 * @param params - Version string parameters
 * @param params.version - The app version number
 * @param params.environment - The current environment name
 * @param params.runtimeVersion - The Expo runtime version
 * @param params.nativeBuildVersion - The native build version number
 * @param params.channel - The release channel name
 * @returns Formatted version string
 */
function buildVersionString({
  version,
  environment,
  runtimeVersion,
  nativeBuildVersion,
  channel,
}: BuildVersionStringParams): string {
  // Compute display environment (strip "production" for cleaner display)
  const displayEnv = (environment ?? "").replace(/production/g, "").trim();

  // Build parts array immutably using conditional spread
  const parts = [
    `v${version}`,
    ...(displayEnv ? [displayEnv] : []),
    ...(runtimeVersion ? [runtimeVersion] : []),
    ...(nativeBuildVersion ? [`Build: ${nativeBuildVersion}`] : []),
    ...(channel ? [`ch:${channel}`] : []),
  ];

  return parts.join(" ");
}

export default buildVersionString;
